import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Grid, InputAdornment,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { getOutsideRentals } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportMoney } from '../../utils/tableExport';

const money = (v) =>
  parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const RentalOutside = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async (q = '') => {
    try {
      setLoading(true);
      setError('');
      const res = await getOutsideRentals(q ? { search: q } : {});
      setRows(res.data || []);
      setTotals(res.totals || null);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load outside rentals'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onBranch = () => load(search);
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Outside rent</Typography>
          <Typography variant="body2" color="text.secondary">
            Profiles for equipment rented from outside — what we have paid and what we will pay.
          </Typography>
        </Box>
        <ExportButtons
          title="Outside Rent"
          filename="outside_rent"
          columns={[
            { label: 'Item', value: (r) => dash(r.product_name) },
            { label: 'Serial', value: (r) => dash(r.serial_number) },
            { label: 'Paid so far', value: (r) => fmtExportMoney(r.paid) },
            { label: 'Will pay', value: (r) => fmtExportMoney(r.total) },
            { label: 'Remaining', value: (r) => fmtExportMoney(r.remaining) },
            { label: 'Customer billed', value: (r) => fmtExportMoney(r.customer_billed) }
          ]}
          rows={rows}
        />
      </Box>

      {totals && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            { label: 'Profiles', value: totals.profiles || 0 },
            { label: 'Paid so far', value: money(totals.paid) },
            { label: 'Will pay (total)', value: money(totals.total) },
            { label: 'Still to pay', value: money(totals.remaining) }
          ].map((card) => (
            <Grid item xs={6} md={3} key={card.label}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h6" fontWeight={700}>{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box component="form" onSubmit={(e) => { e.preventDefault(); load(search.trim()); }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search item, serial, or components"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            )
          }}
        />
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Serial</TableCell>
                <TableCell align="right">Paid so far</TableCell>
                <TableCell align="right">Will pay</TableCell>
                <TableCell align="right">Still to pay</TableCell>
                <TableCell align="right">Customer billed</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary">No outside rentals yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow
                  key={r.profile_key}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/rentals/outside/${encodeURIComponent(r.profile_key)}`)}
                >
                  <TableCell>{r.product_name}</TableCell>
                  <TableCell>{r.serial_number || '—'}</TableCell>
                  <TableCell align="right">{money(r.paid)}</TableCell>
                  <TableCell align="right">{money(r.total)}</TableCell>
                  <TableCell align="right">{money(r.remaining)}</TableCell>
                  <TableCell align="right">{money(r.customer_billed)}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/rentals/outside/${encodeURIComponent(r.profile_key)}`); }}>
                      View profile
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RentalOutside;
